import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { Nullable } from 'global';
import { capitalizeFirstLetter } from 'formatters';
import {
  FilterCardContainer,
  FilterEvent,
  ListOfPresets,
  PresetFilter,
  PresetId,
  PresetMode,
  PresetsForm,
} from 'components/shared/Filters';
import { FilterTableType, TableFilter } from '@trnsact/trnsact-shared-types';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { FinanceFilters, FinanceFilterType, FinanceFiltersContainer } from 'modules/finance/filters';

import { useFinancePresets } from '../../api';

interface PresetModeState {
  type: PresetMode;
  presetId: Nullable<PresetId>;
  filters: Nullable<Partial<FinanceFilters>>;
}

interface Props {
  isFiltersOpen: boolean;
  listOfPresets: TableFilter[];
  currentFilterConfig: Nullable<Partial<FinanceFilters>>;
  setIsFiltersOpen: Dispatch<SetStateAction<boolean>>;
  onTableFiltersChanged: (filters: Partial<FinanceFilters>, event: FilterEvent) => void;
}

const FINANCE_PAGE_FILTERS: FinanceFilterType[] = [
  FinanceFilterType.DEAL_DATE,
  FinanceFilterType.SALES_REP,
  FinanceFilterType.LOCATION,
  FinanceFilterType.PAYMENT_TYPE,
  FinanceFilterType.DEAL_STATUS,
];

export const FinanceTableFilters = ({
  isFiltersOpen,
  listOfPresets = [],
  currentFilterConfig,
  setIsFiltersOpen,
  onTableFiltersChanged,
}: Props) => {
  const { handleCreateFilter, handleUpdateFilter, handleDeleteFilter, isFilterUpdating, isFilterDeleting } =
    useFinancePresets();

  const [externalFiltersValue, setExternalFiltersValue] =
    useState<Nullable<Partial<FinanceFilters>>>(currentFilterConfig);
  const [mode, setMode] = useState<PresetModeState>({ type: PresetMode.OFF, presetId: null, filters: null });

  const presetsMap = listOfPresets.reduce<Record<PresetId, PresetFilter<FinanceFilters>>>(
    (acc, { name, tableFilterId, isDefault, config }) => {
      acc[tableFilterId] = {
        name,
        id: tableFilterId,
        isSelected: isDefault,
        value: config as FinanceFilters,
      };
      return acc;
    },
    {}
  );

  const presetFormVisibleState: PresetMode[] = [PresetMode.CREATE, PresetMode.UPDATE];
  const isPresetFormVisible = presetFormVisibleState.includes(mode.type);

  useEffect(() => {
    setExternalFiltersValue(currentFilterConfig);
  }, [currentFilterConfig]);

  const handleSelectPreset = (presetId: PresetId) => {
    setExternalFiltersValue(presetsMap[presetId].value);
  };

  const handleMoveToCreatePresetMode = (filters: Partial<FinanceFilters>) => {
    setMode({ type: PresetMode.CREATE, filters, presetId: null });
  };

  const handleMoveToEditPresetMode = (presetId: PresetId, filters: Partial<FinanceFilters>) => {
    setMode({ type: PresetMode.UPDATE, filters, presetId });
  };

  const handleMoveToOffPresetMode = () => {
    setMode({ type: PresetMode.OFF, filters: null, presetId: null });
  };

  const handleSavePreset = async (name: string, filters: Partial<FinanceFilters>) => {
    if (mode.type === PresetMode.UPDATE && mode.presetId) {
      await handleUpdateFilter({ name, config: filters, tableFilterId: mode.presetId });
    } else {
      await handleCreateFilter({ name, config: filters, isDefault: false, tableType: FilterTableType.FinanceLog });
    }
    handleMoveToOffPresetMode();
  };

  return (
    <FinanceFiltersContainer
      open={isFiltersOpen}
      onClose={() => setIsFiltersOpen(false)}
      onChanged={onTableFiltersChanged}
      externalMeta={{}}
      configuration={FINANCE_PAGE_FILTERS}
      externalFiltersValue={externalFiltersValue}
      headerNode={filters => (
        <Button
          size="small"
          variant="text"
          onClick={() => handleMoveToCreatePresetMode(filters)}
          disabled={presetFormVisibleState.includes(mode.type)}
        >
          Save Filter View
        </Button>
      )}
      presetsNode={filters => (
        <Stack gap="0.5rem">
          <RenderWithCondition condition={isPresetFormVisible}>
            <FilterCardContainer label={`${capitalizeFirstLetter(mode.type)} Filter`}>
              <PresetsForm<Partial<FinanceFilters>>
                key={mode.type}
                onCancel={handleMoveToOffPresetMode}
                preset={mode.presetId ? presetsMap[mode.presetId] : null}
                onSave={({ name }) => handleSavePreset(name, filters)}
              />
            </FilterCardContainer>
          </RenderWithCondition>

          <FilterCardContainer label="Saved Filters">
            <ListOfPresets<Partial<FinanceFilters>>
              list={Object.values(presetsMap)}
              actionsLoading={isFilterUpdating || isFilterDeleting}
              actions={{
                onDelete: async presetId => {
                  await handleDeleteFilter(presetId);
                },
                onSelect: handleSelectPreset,
                onCreate: () => handleMoveToCreatePresetMode(filters),
                onEdit: presetId => handleMoveToEditPresetMode(presetId, filters),
              }}
            />
          </FilterCardContainer>
        </Stack>
      )}
    />
  );
};
